/**
 * Utility functions for handling rental price operations
 */

/**
 * Convert rental period to days for comparison
 * @param {number} number - The number of periods
 * @param {string} option - The period type ('day', 'week', 'month')
 * @returns {number} - Total days
 */
export const convertToDays = (number, option) => {
  switch (option?.toLowerCase()) {
    case 'day':
    case 'days':
      return number;
    case 'week':
    case 'weeks':
      return number * 7;
    case 'month':
    case 'months':
      return number * 30;
    default:
      return number;
  }
};

/**
 * Limit rental options to a maximum number, selecting the most relevant ones
 * @param {Array} rentPriceAndTime - Array of rental price options
 * @param {number} maxOptions - Maximum number of options to return (default: 3)
 * @returns {Array} - Limited array of rental options
 */
export const limitRentalOptions = (rentPriceAndTime, maxOptions = 3) => {
  if (!rentPriceAndTime || !Array.isArray(rentPriceAndTime)) {
    return [];
  }
  
  if (rentPriceAndTime.length <= maxOptions) {
    return rentPriceAndTime;
  }
  
  // Sort by duration (shortest to longest)
  const sorted = [...rentPriceAndTime].sort((a, b) => {
    const aDays = convertToDays(a.number, a.option);
    const bDays = convertToDays(b.number, b.option);
    return aDays - bDays;
  });
  
  if (maxOptions === 3 && sorted.length >= 3) {
    // Return shortest, middle, and longest
    return [
      sorted[0], // Shortest
      sorted[Math.floor(sorted.length / 2)], // Middle
      sorted[sorted.length - 1] // Longest
    ];
  }
  
  // For other maxOptions values, just return the first N
  return sorted.slice(0, maxOptions);
};

/**
 * Format rental period for display
 * @param {Object} rentalOption - Rental option object with number and option
 * @returns {string} - Formatted period string
 */
export const formatRentalPeriod = (rentalOption) => {
  if (!rentalOption || !rentalOption.number || !rentalOption.option) {
    return 'Unknown period';
  }
  
  const { number, option } = rentalOption;
  const pluralSuffix = number > 1 ? 's' : '';
  
  return `${number} ${option}${pluralSuffix}`;
};

/**
 * Validate rental price array for backend use
 * @param {Array} rentPriceAndTime - Array of rental price options
 * @param {number} maxAllowed - Maximum allowed options (default: 3)
 * @returns {Object} - Validation result with isValid and message
 */
export const validateRentalPrices = (rentPriceAndTime, maxAllowed = 3) => {
  if (!rentPriceAndTime || !Array.isArray(rentPriceAndTime)) {
    return { isValid: true, message: 'No rental prices to validate' };
  }
  
  if (rentPriceAndTime.length > maxAllowed) {
    return {
      isValid: false,
      message: `Too many rental options. Maximum allowed: ${maxAllowed}, provided: ${rentPriceAndTime.length}`
    };
  }
  
  // Validate each option has required fields
  for (let i = 0; i < rentPriceAndTime.length; i++) {
    const option = rentPriceAndTime[i];
    if (!option.number || !option.option || !option.price) {
      return {
        isValid: false,
        message: `Invalid rental option at index ${i}. Required fields: number, option, price`
      };
    }
  }
  
  return { isValid: true, message: 'Rental prices are valid' };
};